import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import API from '../../services/api.js'
import { useQuestionnaires } from '../../hooks/useQuestionnaires.js'
import { Plus, Download, Upload, X, CheckCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

// ─────────────────────────────────────────────────────────────────
// Excel Parsing — inline, no external utility dependency
// ─────────────────────────────────────────────────────────────────

const VALID_QUESTION_TYPES = ['text', 'yes_no', 'multiple_choice', 'scale']

function findQuestionnaireSheet(workbook) {
  for (const sheetName of workbook.SheetNames) {
    if (sheetName.toLowerCase().includes('questionnaire')) {
      const sheet = workbook.Sheets[sheetName]
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
      return { sheet, sheetName, data }
    }
  }

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName]
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
    for (const row of data.slice(0, 10)) {
      for (const cell of row) {
        const c = String(cell).toLowerCase().trim()
        if (c === 'control description' || c === 'question text' || c === 'description') {
          return { sheet, sheetName, data }
        }
      }
    }
  }

  const sheetName = workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]
  const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
  return { sheet, sheetName, data }
}

function findHeaderRowIndex(data) {
  for (let i = 0; i < Math.min(data.length, 10); i++) {
    for (const cell of data[i]) {
      const c = String(cell).toLowerCase().trim()
      if (
        c === 'control description' ||
        c === 'question text' ||
        c === 'description' ||
        c === 'question'
      ) {
        return i
      }
    }
  }
  return 0
}

function mapColumns(headerRow) {
  const map = {
    domain:      -1,
    requirement: -1,
    description: -1,
    type:        -1,
    options:     -1,
    hint:        -1,
  }
  headerRow.forEach((cell, i) => {
    const h = String(cell).toLowerCase().trim()
    if (h.includes('domain')) map.domain = i
    else if (h.includes('requirement')) map.requirement = i
    else if (
      h.includes('control description') ||
      h.includes('question text') ||
      h.includes('description')
    ) {
      if (map.description === -1) map.description = i
    } else if (h.includes('type') && !h.includes('data')) map.type = i
    else if (h.includes('option')) map.options = i
    else if (h.includes('hint')) map.hint = i
  })
  return map
}

function parseExcelFile(file, arrayBuffer) {
  const errors = []

  let workbook
  try {
    workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' })
  } catch (err) {
    return { title: '', description: '', questions: [], errors: [`Could not read file: ${err.message}`] }
  }

  const { sheetName, data } = findQuestionnaireSheet(workbook)
  const headerRowIdx = findHeaderRowIndex(data)
  const headerRow = data[headerRowIdx]
  const colMap = mapColumns(headerRow)

  if (colMap.description === -1) {
    errors.push(
      'Could not find a "Control Description" or "Question Text" column. ' +
      'Please make sure your file matches the template format.'
    )
    return { title: '', description: '', questions: [], errors }
  }

  const questions = []

  for (let i = headerRowIdx + 1; i < data.length; i++) {
    const row = data[i]
    const questionText = String(row[colMap.description] ?? '').trim()
    if (!questionText) continue

    let questionType = 'text'
    if (colMap.type !== -1) {
      const raw = String(row[colMap.type] ?? '').toLowerCase().trim()
      if (VALID_QUESTION_TYPES.includes(raw)) questionType = raw
    }

    let options = null
    if (questionType === 'multiple_choice') {
      if (colMap.options !== -1) {
        const rawOptions = String(row[colMap.options] ?? '').trim()
        if (rawOptions) {
          options = rawOptions.split('|').map(o => o.trim()).filter(Boolean)
        }
      }
      if (!options || options.length < 2) {
        options = ['Yes', 'No', 'Partial', 'Not Applicable']
      }
    }

    const hintParts = []
    if (colMap.domain !== -1 && row[colMap.domain]) hintParts.push(String(row[colMap.domain]).trim())
    if (colMap.requirement !== -1 && row[colMap.requirement]) hintParts.push(String(row[colMap.requirement]).trim())
    let hint = hintParts.join(' · ')
    if (colMap.hint !== -1 && row[colMap.hint]) {
      const hintText = String(row[colMap.hint]).trim()
      hint = hint ? `${hint} — ${hintText}` : hintText
    }

    questions.push({
      question_text: questionText,
      question_type: questionType,
      options,
      hint: hint || null,
      order_index: questions.length,
    })
  }

  if (questions.length === 0) {
    errors.push(
      `No questions found in sheet "${sheetName}". ` +
      'Make sure the Control Description column contains question text.'
    )
  }

  const title = file.name
    .replace(/\.(xlsx?|xls)$/i, '')
    .replace(/[_-]+/g, ' ')
    .trim()

  return { title, description: '', questions, errors }
}

// ─────────────────────────────────────────────────────────────────
// Upload Progress / Result Modal
// ─────────────────────────────────────────────────────────────────
function UploadModal({ state, onClose, onNavigate }) {
  if (!state) return null

  const { status, errors, title, count } = state
  const isLoading = status === 'parsing' || status === 'importing'

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={!isLoading ? onClose : undefined}
        />

        <motion.div
          className="relative z-10 w-full max-w-md bg-[#0f1623] border border-white/10 rounded-2xl p-6 shadow-2xl"
          initial={{ scale: 0.95, y: 12 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 12 }}
          transition={{ duration: 0.25 }}
        >
          {!isLoading && (
            <button
              type='button'
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {isLoading && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <p className="text-sm text-gray-300">
                {status === 'parsing' ? 'Reading template…' : 'Creating questionnaire…'}
              </p>
            </div>
          )}

          {status === 'error' && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                <h3 className="text-sm font-semibold text-white">
                  {errors?.length === 1 ? '1 issue found' : `${errors?.length} issues found`}
                </h3>
              </div>
              <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {errors?.map((err, i) => (
                  <li
                    key={i}
                    className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 leading-relaxed"
                  >
                    {err}
                  </li>
                ))}
              </ul>
              <button
                type='button'
                onClick={onClose}
                className="mt-5 w-full py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition"
              >
                Fix & re-upload
              </button>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 text-center py-2">
              <CheckCircle className="w-10 h-10 text-green-400" />
              <div>
                <p className="text-sm font-semibold text-white">{title}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {count} question{count !== 1 ? 's' : ''} imported successfully
                </p>
              </div>
              <div className="flex gap-3 w-full mt-2">
                <button
                  type='button'
                  onClick={onClose}
                  className="flex-1 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 transition"
                >
                  Stay here
                </button>
                <button
                  type='button'
                  onClick={onNavigate}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm text-white font-medium transition"
                >
                  Open questionnaire
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// ─────────────────────────────────────────────────────────────────
// Status Badge
// ─────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const styles = {
    draft:    'bg-gray-500/10 text-gray-400 border border-gray-500/20',
    active:   'bg-green-500/10 text-green-400 border border-green-500/20',
    archived: 'bg-gray-500/10 text-gray-500 border border-gray-500/20',
  }
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10px] font-medium inline-flex items-center ${styles[status] ?? styles.draft}`}
    >
      {status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
export default function Questionnaires() {
  const navigate = useNavigate()

  // Page-level asset scope — same sessionStorage → /resolve-asset-id/
  // pattern used across every other GRC page. Without this,
  // useQuestionnaires() never receives an assetId and its internal
  // fetch guard silently skips the request entirely.
  const selectedAppName = sessionStorage.getItem('selectedApp')
  const [assetId, setAssetId] = useState(null)
  const [resolvingAsset, setResolvingAsset] = useState(true)
  const [assetError, setAssetError] = useState(null)

  useEffect(() => {
    if (!selectedAppName) {
      setAssetError('No asset selected — navigate to this page via an asset.')
      setResolvingAsset(false)
      return
    }
    setResolvingAsset(true)
    API.get('/resolve-asset-id/', { params: { name: selectedAppName } })
      .then(({ data }) => {
        setAssetId(data.id)
        setAssetError(null)
      })
      .catch(() => {
        setAssetError('Could not resolve the selected asset. Please re-select it from the dashboard.')
      })
      .finally(() => setResolvingAsset(false))
  }, [selectedAppName])

  const { questionnaires, loading, error, createQuestionnaire } = useQuestionnaires(assetId)

  const [currentUser, setCurrentUser] = useState(null)
  const [uploadState, setUploadState] = useState(null)
  const [newQId, setNewQId]           = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    async function fetchUser() {
      try {
        const { data } = await API.get('/auth/me/')
        setCurrentUser(data)
      } catch (err) {
        console.error('Failed to fetch current user:', err?.response?.data?.detail || err.message)
      }
    }
    fetchUser()
  }, [])

  const canEdit = currentUser?.role === 'super_admin' || currentUser?.role === 'client_admin'

  function downloadTemplate() {
    const a = document.createElement('a')
    a.href = '/AI_Risk_Assessment_Questionnaire_v3-2.xlsx'
    a.download = 'AI_Risk_Assessment_Questionnaire_v3.xlsx'
    a.click()
  }

  // ── Upload handler ─────────────────────────────────────────────
  async function handleFileChange(e) {
    const file = e.target.files?.[0]
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!file) return

    if (!file.name.match(/\.xlsx?$/i)) {
      setUploadState({
        status: 'error',
        errors: ['Please upload an .xlsx file.'],
      })
      return
    }

    if (!assetId) {
      setUploadState({
        status: 'error',
        errors: ['No asset selected — cannot import a questionnaire without an asset.'],
      })
      return
    }

    try {
      // 1. Parse
      setUploadState({ status: 'parsing' })
      const arrayBuffer = await file.arrayBuffer()
      const { title, description, questions, errors } = parseExcelFile(file, arrayBuffer)

      if (errors.length > 0) {
        setUploadState({ status: 'error', errors })
        return
      }

      // 2. Create the questionnaire via Django (asset_id applied inside the hook)
      setUploadState({ status: 'importing' })

      const questionnaire = await createQuestionnaire(title, description)

      // 3. Add each question via Django — no more direct table inserts.
      for (const q of questions) {
        await API.post(`/questionnaires/${questionnaire.id}/questions/`, {
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options ?? null,
          hint: q.hint ?? null,
          asset_id: assetId,
        })
      }

      setNewQId(questionnaire.id)
      setUploadState({ status: 'success', title, count: questions.length })

    } catch (err) {
      setUploadState({
        status: 'error',
        errors: [err?.response?.data?.detail || err.message || 'An unexpected error occurred. Please try again.'],
      })
    }
  }
  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative w-full max-w-[1400px] mx-auto px-3 md:px-4 lg:px-6 py-6 sm:py-8 min-w-0">

        {/* ── Page Header ──────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">

          {/* Left side */}
          <div className="flex items-center gap-4">
            <motion.button
              onClick={() => navigate('/grc/framework-review')}
              title="Go back"
              whileHover={{ scale: 1.08, x: -2 }}
              whileTap={{ scale: 0.92 }}
              transition={{ type: 'spring', stiffness: 260, damping: 16 }}
              className="flex items-center justify-center text-gray-400 hover:text-white transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </motion.button>

            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white tracking-tight">
                Questionnaires
              </h1>
              <div className="mt-2 h-[2px] w-20 sm:w-40 bg-gradient-to-r from-blue-500/60 via-blue-400/10 to-transparent animate-pulse" />
            </div>
          </div>

          {/* Right side */}
          {canEdit && (
            <div className="flex items-center gap-2">

              <motion.button
                onClick={downloadTemplate}
                title="Download Excel template"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.94 }}
                transition={{ type: 'spring', stiffness: 240, damping: 14 }}
                className="
                  inline-flex items-center gap-2
                  px-3 py-2 rounded-lg text-xs sm:text-sm font-medium
                  bg-white/5 border border-white/10 text-gray-300
                  hover:bg-white/10 hover:border-gray-500/40 hover:text-white
                  transition-colors duration-200
                "
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Template</span>
              </motion.button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={handleFileChange}
              />

              <motion.button
                onClick={() => fileInputRef.current?.click()}
                title="Import questionnaire from Excel"
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.94 }}
                transition={{ type: 'spring', stiffness: 240, damping: 14 }}
                className="
                  inline-flex items-center gap-2
                  px-3 py-2 rounded-lg text-xs sm:text-sm font-medium
                  bg-white/5 border border-white/10 text-gray-300
                  hover:bg-white/10 hover:border-gray-500/40 hover:text-white
                  transition-colors duration-200
                "
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Import</span>
              </motion.button>

              <motion.button
                onClick={() => navigate('/grc/questionnaires/new')}
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.94 }}
                transition={{ type: 'spring', stiffness: 240, damping: 14 }}
                className="
                  inline-flex items-center gap-2
                  px-3 sm:px-4 py-2 rounded-lg
                  text-xs sm:text-sm font-medium
                  bg-white/5 border border-white/10 text-gray-300
                  hover:bg-white/10 hover:border-gray-500/40 hover:text-white
                  transition-colors duration-200
                "
              >
                <Plus className="w-4 h-4" />
                <span>New</span>
              </motion.button>

            </div>
          )}

        </div>

        {assetError && (
          <div className="mb-6 flex items-start gap-2 px-4 py-3 rounded-lg bg-red-500/5 border border-red-500/20">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{assetError}</p>
          </div>
        )}

        {/* ── Content ──────────────────────────────────────────── */}
        {resolvingAsset || loading ? (
          <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Loading...</p>
            </div>
          </div>
        ) : error ? (
          <p className="text-red-400 text-sm">{error}</p>
        ) : questionnaires.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <p className="text-base text-gray-300">No questionnaires yet</p>
            <p className="text-sm mt-2">
              Create one with <strong>New</strong> or import from an Excel template.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {questionnaires.map((q, i) => (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: i * 0.04 }}
                className="
                  group cursor-pointer
                  bg-white/5 backdrop-blur-xl border border-white/10
                  rounded-xl px-5 py-4
                  hover:bg-white/[0.07] hover:border-white/20
                  transition-all duration-200
                "
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{q.title}</p>
                    {q.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">{q.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <StatusBadge status={q.status} />
                      {q.created_by_name && (
                        <span className="text-[10px] text-gray-600">
                          by {q.created_by_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/grc/questionnaires/${q.id}`)
                    }}
                    className="
                      px-3 py-1.5 text-xs
                      bg-white/5 border border-white/10
                      rounded-md text-gray-300
                      hover:text-white hover:border-gray-500/50 hover:bg-white/10
                      whitespace-nowrap
                    "
                  >
                    View
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

      </div>

      {/* ── Upload Modal ─────────────────────────────────────── */}
      <UploadModal
        state={uploadState}
        onClose={() => { setUploadState(null); setNewQId(null) }}
        onNavigate={() => { setUploadState(null); navigate(`/grc/questionnaires/${newQId}`) }}
      />
    </div>
  )
}